// Dispatch documents for ONE delivery challan — payment proof, LR / e-way bill
// and delivery evidence. Billing invoices belong to Sales/Accounts, not Dispatch.
//
// This replaced a window.prompt() that asked the user to TYPE 'payment_proof'
// or 'lr' as a string (a typo silently filed the file under "other"), followed
// by an invisible file input. Nothing showed what was already attached, and a
// wrong file could never be taken back off.
//
// Reachable by Sales as well as Dispatch and Accounts — the same three
// departments the challan list itself admits — because attaching the LR or the
// customer's payment proof is shared work. requireDispatchDocumentUpload on the
// API is the real gate.

import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, FileText, Trash2, Upload, ExternalLink } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { ROUTES } from '@/routes/paths';
import { dispatchDocChecklist, dispatchDocsAllClear } from '../dispatch-docs';

/** Mirrors the schema enum in src/db-accounts.js — the API files anything else
 *  under 'other', so the picker offers exactly these and nothing more. */
const KINDS = [
  { value: 'payment_proof', label: 'Payment proof' },
  { value: 'lr', label: 'LR / e-way bill' },
  { value: 'other', label: 'Other' },
] as const;

type Kind = (typeof KINDS)[number]['value'];

interface DispatchDocument {
  _id: string;
  // Wider than `Kind` (the upload picker's own choices) on purpose — a file
  // can also arrive with kind 'invoice', auto-attached elsewhere rather than
  // picked here, and the list below explicitly filters those out.
  kind: Kind | 'invoice';
  fileUrl: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
}

interface Challan {
  _id: string;
  documentNumber: string;
  date: string;
  status: string;
  customerId?: { name?: string } | null;
  dispatchDocuments?: DispatchDocument[];
}

const API = '/api/accounts/commercial-documents/delivery-challans';
const MAX_MB = 25;

function fileSize(bytes = 0): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const kindLabel = (kind: string) => KINDS.find((k) => k.value === kind)?.label ?? 'Other';

export function DispatchChallanDocumentsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<Kind>('payment_proof');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<DispatchDocument | null>(null);

  const challan = useQuery({
    queryKey: ['accounts', 'commercial-documents', 'delivery-challans', id],
    queryFn: async () => (await api.get<Challan>(`${API}/${id}`)).data,
    enabled: !!id,
  });

  const attached = useMemo(() => (challan.data?.dispatchDocuments ?? []).filter((file) => file.kind !== 'invoice'), [challan.data]);
  // Checklist item 52 — name the documents a dispatch must carry and say which
  // are missing, instead of leaving the reader to infer it from a filename list.
  // Reads EVERY attachment, including the auto-attached invoice that `attached`
  // deliberately hides.
  const docChecks = useMemo(() => dispatchDocChecklist(challan.data?.dispatchDocuments), [challan.data]);
  const docsAllClear = dispatchDocsAllClear(challan.data?.dispatchDocuments);
  const refresh = () => qc.invalidateQueries({ queryKey: ['accounts'] });

  /** Each file goes to /api/upload for a URL, then the whole batch is attached
   *  in ONE call — the API appends, so a single request leaves one audit entry
   *  per upload rather than one per file. */
  const upload = async (chosen: File[]) => {
    const tooBig = chosen.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than ${MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const files = [];
      for (const file of chosen) {
        const body = new FormData();
        body.append('file', file);
        const { data } = await api.post<{ url: string }>('/api/upload', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) {
          files.push({ kind, fileUrl: data.url, originalName: file.name, mimeType: file.type, size: file.size });
        }
      }
      if (files.length === 0) throw new Error('Nothing was uploaded.');
      await api.post(`${API}/${id}/dispatch-documents`, { files });
      refresh();
      toast.success(files.length === 1 ? 'Document attached.' : `${files.length} documents attached.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not attach the document.'));
    } finally {
      setUploading(false);
    }
  };

  const remove = useMutation({
    mutationFn: (docId: string) => api.delete(`${API}/${id}/dispatch-documents/${docId}`),
    onSuccess: () => { refresh(); setRemoving(null); toast.success('Document removed.'); },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not remove the document.')),
  });

  const doc = challan.data;
  const isIssued = Boolean(doc?.documentNumber && !doc.documentNumber.startsWith('DRAFT-'));

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.dispatchChallans)}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Delivery Challans
        </Button>
        <div>
          <h1 className="text-xl font-bold tracking-tight">Dispatch Documents</h1>
          {challan.isLoading ? (
            <Skeleton className="mt-1 h-4 w-64" />
          ) : doc ? (
            <p className="text-xs text-muted-foreground">
              {isIssued ? doc.documentNumber : 'Dispatch record'}
              {doc.customerId?.name ? ` · ${doc.customerId.name}` : ''}
              {doc.date ? ` · ${new Date(doc.date).toLocaleDateString('en-IN')}` : ''}
              {doc.status ? ` · ${doc.status.replaceAll('_', ' ')}` : ''}
            </p>
          ) : null}
        </div>
      </div>

      {challan.isError ? (
        <Card><CardBody><p className="text-destructive">Could not load this challan.</p></CardBody></Card>
      ) : (
        <>
          <Card>
            <CardHeader title="Attach a document" />
            <CardBody className="space-y-4">
              {/* `options`, not <option> children: the shared Select only
                  renders SelectItem descriptors or this prop, and silently
                  drops anything else — which is how this rendered empty. */}
              <div className="max-w-xs">
                <Select
                  label="Document type"
                  value={kind}
                  options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
                  onChange={(e) => setKind(e.target.value as Kind)}
                />
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  const dropped = Array.from(e.dataTransfer.files || []);
                  if (dropped.length) void upload(dropped);
                }}
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? 'border-primary bg-primary/5' : 'border-outline'}`}
              >
                <Upload className="h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">
                  Drop one or more files here, or{' '}
                  <button
                    type="button"
                    className="text-primary underline-offset-2 hover:underline"
                    onClick={() => fileRef.current?.click()}
                  >
                    choose files
                  </button>
                </p>
                <p className="text-xs text-muted-foreground">
                  Several at once is fine — hold Ctrl (or Shift) in the picker to select more than one.
                  PDF or images, up to {MAX_MB} MB each. All of them will be filed as {kindLabel(kind)}.
                </p>
                {uploading && <p className="text-xs font-semibold text-primary">Uploading...</p>}
              </div>

              <input
                ref={fileRef}
                type="file"
                multiple
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const chosen = Array.from(e.target.files || []);
                  e.target.value = '';
                  if (chosen.length) void upload(chosen);
                }}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={`Required documents (${docChecks.filter((c) => c.present).length}/${docChecks.length})`} />
            <CardBody className="p-0">
              {challan.isLoading ? (
                <Skeleton className="h-28" />
              ) : (
                <>
                  <ul className="divide-y divide-outline">
                    {docChecks.map((check) => (
                      <li key={check.kind} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                        <span className="text-sm font-medium text-on-surface">{check.label}</span>
                        <span
                          className={`text-xs font-semibold ${check.present ? 'text-success-text' : 'text-danger-text'}`}
                        >
                          {check.present
                            ? `Attached · ${check.files.slice(0, 2).join(', ')}${check.files.length > 2 ? ` +${check.files.length - 2}` : ''}`
                            : 'Missing'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="px-4 py-3">
                    {docsAllClear ? (
                      <p className="text-sm font-semibold text-success-text">All clear — every required document is attached.</p>
                    ) : (
                      <p className="text-sm text-danger-text">
                        Still needed: {docChecks.filter((c) => !c.present).map((c) => c.label).join(', ')}.
                      </p>
                    )}
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={`Attached (${attached.length})`} />
            <CardBody className="p-0">
              {challan.isLoading ? (
                <Skeleton className="h-32" />
              ) : attached.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  Nothing attached yet. Payment proof, the LR or e-way bill and delivery proof belong here.
                </p>
              ) : (
                <ul className="divide-y divide-outline">
                  {attached.map((file) => (
                    <li key={file._id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{file.originalName || 'Uploaded file'}</p>
                          <p className="text-xs text-muted-foreground">
                            {kindLabel(file.kind)}
                            {fileSize(file.size) ? ` · ${fileSize(file.size)}` : ''}
                            {file.uploadedAt ? ` · ${new Date(file.uploadedAt).toLocaleDateString('en-IN')}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => window.open(file.fileUrl, '_blank', 'noopener,noreferrer')}>
                          <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRemoving(file)}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </>
      )}

      <ConfirmDialog
        open={!!removing}
        title="Remove this document?"
        message={`"${removing?.originalName || 'This file'}" will be taken off this challan. The removal is recorded in the audit log.`}
        confirmLabel="Remove"
        danger
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing._id)}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}

export default DispatchChallanDocumentsPage;
